import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { TextField } from '../../../../components/inputs/TextField';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { pressReleaseChannelsApi } from '../pressReleaseApi';
import type { PressReleaseChannelListItem } from '../pressReleaseTypes';
import { sampleChannel } from '../utils/sampleChannel';

/** Create or edit one channel — Routing's document-type form, for Public Relations › Settings. */
export function ChannelModal({
  editing,
  onClose,
  onSaved,
}: {
  /** Null creates. */
  editing: PressReleaseChannelListItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();

  // Seeded once, on mount — the parent mounts this only while it is open.
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const canSave = name.trim().length > 0 && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = { name: name.trim(), description: description.trim() || null };

    try {
      if (editing) await pressReleaseChannelsApi.update(editing.id, request);
      else await pressReleaseChannelsApi.create(request);

      toast.success(editing ? `Saved ${request.name}.` : `Added ${request.name}.`);
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the channel.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'press-release-channel' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit channel' : 'New channel'}
      size="lg"
      id="press-release-channel-modal"
      autogenerate={{
        onGenerate: () => {
          const sample = sampleChannel();
          setName(sample.name);
          setDescription(sample.description ?? '');
        },
        disabled: saving,
      }}
      confirm={{ id: 'press-release-channel-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="press-release-channel-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="press-release-channel-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <TextField
          id="press-release-channel-name"
          label="Name"
          required
          description="Where a press release is posted, e.g. Facebook, Website or Instagram. Every live channel is a line on every published press release's checklist."
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          disabled={saving}
          error={error?.fieldError('name')}
        />

        <TextField
          id="press-release-channel-description"
          label="Description"
          description="Optional note for whoever maintains the list — the page or account it means."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          disabled={saving}
          error={error?.fieldError('description')}
        />
      </div>
    </Modal>
  );
}
